import { useEffect, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { LIVE_URL } from "../../../config/config";
import useAuthToken from "../../../hooks/useAuthToken";
import Spinner from "../spinner/Spinner";
import styles from "./css/AddSubCategory.module.scss";

const AddSubCategory = () => {
  const { token } = useAuthToken();
  const { id } = useParams();

  const [title, setTitle] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Function to reset form fields
  const resetForm = () => {
    setTitle("");
    setSelectedCategory("");
    setSelectedStatus("");
    setPopupMessage("");
    setErrors({});
  };

  // Fetch categories for the dropdown
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${LIVE_URL}/admin/category/list?`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        setCategories(data.data.category || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };
  useEffect(() => {
    fetchCategories();
  }, [token]);

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  useEffect(() => {
    if (id) {
      const fetchSubCategoryData = async () => {
        try {
          setLoading(true);
          const response = await fetch(
            `${LIVE_URL}/admin/sub-category/get/${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          const data = await response.json();
          if (response.ok) {
            const subCategoryData = data.data;
            setTitle(subCategoryData.title);
            setSelectedCategory(subCategoryData.category_id);
            setSelectedStatus(subCategoryData.status ? "true" : "false");
          }
        } catch (error) {
          console.error("Error fetching sub-category data:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchSubCategoryData();
    } else {
      resetForm();
      // setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    let timer;
    if (popupMessage) {
      const popupElement = document.getElementById("popup-message");
      if (popupElement) {
        popupElement.style.opacity = "1";
      }
      timer = setTimeout(() => {
        if (popupElement) {
          popupElement.style.opacity = "0";
        }
        setTimeout(() => {
          setPopupMessage("");
        }, 500);
      }, 2000);
    }
    return () => clearTimeout(timer);
  }, [popupMessage]);

  const handleSelectChange = (event) => {
    setSelectedStatus(event.target.value);
    setErrors((prevErrors) => ({ ...prevErrors, selectedStatus: "" }));
  };

  const handleTitleChange = (event) => {
    setTitle(event.target.value);
    setErrors((prevErrors) => ({ ...prevErrors, title: "" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const newErrors = {};
    if (!title) newErrors.title = "❗ Don't Forget the Title!";
    if (!selectedCategory)
      newErrors.selectedCategory = "❗Category field cannot be blank.";
    if (!selectedStatus)
      newErrors.selectedStatus = "❗Status field cannot be blank.";
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setPopupMessage("Please fill in all required fields");
      setLoading(false);
      return;
    }

    const formData = {
      id,
      title,
      status: selectedStatus,
      category_id: parseInt(selectedCategory),
    };

    try {
      const url = id
        ? `${LIVE_URL}/admin/sub-category/update`
        : `${LIVE_URL}/admin/sub-category/create`;

      const method = "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const successMessage = id
          ? "sub-category updated successfully."
          : "sub-category added successfully.";
        navigate(
          `/home/list-sub-category?message=${encodeURIComponent(
            successMessage,
          )}`,
        );
      } else {
        setPopupMessage(
          `Failed to save sub-category. Status: ${response.status}`,
        );
      }
    } catch (error) {
      setPopupMessage("Error saving sub-category.");
    } finally {
      setLoading(false);
    }
  };

  const options = [
    { value: "true", label: "Publish" },
    { value: "false", label: "UnPublish" },
  ];

  if (loading) {
    return <Spinner />;
  }
  return (
    <>
      <div
        id="popup-message"
        className={`${styles.update_message} ${popupMessage.includes(
          "success",
        )}`}
      >
        {popupMessage && <p>{popupMessage}</p>}
      </div>
      <div className={`${styles.add_sub_category} `}>
        <header>
          <h2>{id ? "Update Sub-Category" : "Add Sub-Category"}</h2>
        </header>
        <section className={`${styles.section}`}>
          <label htmlFor="title">Select Category</label>
          <br />

          <div className={styles.select_container}>
            <select value={selectedCategory} onChange={handleCategoryChange}>
              <option value="">Select Category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.title}
                </option>
              ))}
            </select>
            <FaChevronDown className={`${styles.chevron_down}`} />
          </div>
          {errors.selectedCategory && (
            <p className={styles.error}>{errors.selectedCategory}</p>
          )}
          <br />
          <br />
          <label htmlFor="title">Add Sub-Category</label>
          <br />
          <input
            type="text"
            id="title"
            value={title}
            onChange={handleTitleChange}
          />
          {errors.title && <p className={styles.error}>{errors.title}</p>}
          <br />
          <br />
          <label htmlFor="status">Sub-Category Status</label>
          <br />
          <div className={`${styles.select_container}`}>
            <select value={selectedStatus} onChange={handleSelectChange}>
              <option value="">Select Status</option>
              {options.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <FaChevronDown className={`${styles.chevron_down}`} />
          </div>
          {errors.selectedStatus && (
            <p className={styles.error}>{errors.selectedStatus}</p>
          )}
          <br />
          <div className={`${styles.button_container}`}>
            <button onClick={() => navigate("/home/list-sub-category")}>
              Cancel
            </button>
            <button onClick={handleSubmit}>
              {id ? "Update Sub-Category" : "Add Sub-Category"}
            </button>
          </div>
        </section>
      </div>
    </>
  );
};

export default AddSubCategory;
